import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Loader2, Landmark } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [signature, setSignature] = useState("");
  const [inviteCode, setInviteCode] = useState(searchParams.get("invite") || "");
  const [termsSummary, setTermsSummary] = useState("");
  const [legalDocs, setLegalDocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/terms")
      .then(({ data }) => setTermsSummary(data.summary))
      .catch(() => {});
    api
      .get("/legal/documents")
      .then(({ data }) => setLegalDocs(data.documents || []))
      .catch(() => {});
  }, []);

  const registerReady = acceptTerms && signature.trim().includes(" ") && signature.trim().length >= 3;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (mode === "register" && !registerReady) {
      setError("Agree to all listed agreements and type your full legal name as your electronic signature.");
      return;
    }
    setSubmitting(true);
    try {
      let authedUser;
      if (mode === "login") {
        authedUser = await login(email, password);
        toast.success("Welcome back.");
      } else {
        const result = await register(email, password, {
          acceptTerms,
          signature: signature.trim(),
          inviteCode: inviteCode.trim(),
        });
        authedUser = result.user;
        if (result.trialActivated) {
          toast.success(
            `Beta invite accepted — your free Enterprise trial is active until ${new Date(
              result.trialEndsAt
            ).toLocaleDateString()}. Report requests are billed separately during the trial.`,
            { duration: 12000 }
          );
        } else {
          toast.success("Account created. Welcome to GetYourQSIP.");
        }
      }
      const next = searchParams.get("next");
      navigate(next || (authedUser?.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      const message = apiErrorMessage(err, "Authentication failed.");
      const retry = err?.response?.data?.retryAfterSeconds;
      setError(retry ? `${message} Try again in ${Math.ceil(retry / 60)} minute(s).` : message);
    } finally {
      setSubmitting(false);
    }
  };

  const sharedFields = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          data-testid="auth-email-input"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          required
          value={password}
          data-testid="auth-password-input"
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Minimum 8 characters"
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="space-y-2">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
              <Landmark className="h-5 w-5 text-primary" />
            </span>
            <CardTitle className="font-display text-2xl">Member access</CardTitle>
            <CardDescription>Sign in to your dashboard or create a new GetYourQSIP account.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={mode} onValueChange={(value) => { setMode(value); setError(""); }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="auth-login-tab">Sign in</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="pt-4">
                <form onSubmit={submit} className="space-y-4" noValidate>
                  {sharedFields}
                  {error && (
                    <p className="text-sm text-destructive" role="alert" data-testid="auth-error-message">{error}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="auth-submit-button">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="pt-4">
                <form onSubmit={submit} className="space-y-4" noValidate>
                  {sharedFields}

                  <div className="space-y-1.5">
                    <Label htmlFor="invite-code">Beta invite code (optional)</Label>
                    <Input
                      id="invite-code"
                      value={inviteCode}
                      data-testid="register-invite-code-input"
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      placeholder="QSIP-BETA-XXXXX"
                      className="font-mono uppercase"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Have a beta invite? It unlocks a free 2-week Enterprise membership trial. Report
                      requests are billed separately during the trial.
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-secondary/40 p-3.5 space-y-3" data-testid="register-terms-box">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {termsSummary ||
                        "This is not legal advice. We are not attorneys or licensed brokers. This service is for educational and research purposes only. You agree to hold GetYourQSIP harmless and to indemnify it. We recommend all documents be reviewed by the appropriate licensed professional and/or attorney."}
                    </p>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-foreground">
                        Creating an account requires your electronic signature on all of the following agreements:
                      </p>
                      <div
                        className="max-h-36 overflow-y-auto rounded-md border border-border bg-background/60 p-2.5 space-y-1"
                        data-testid="register-agreements-list"
                      >
                        {(legalDocs.length > 0
                          ? legalDocs
                          : [{ slug: "terms-of-service", title: "Terms of Service & User Agreement" }]
                        ).map((doc, index) => (
                          <Link
                            key={doc.slug}
                            to={doc.slug === "terms-of-service" ? "/terms" : `/legal/${doc.slug}`}
                            target="_blank"
                            data-testid={`register-agreement-link-${doc.slug}`}
                            className="block text-[11px] text-primary hover:underline leading-snug"
                          >
                            {index + 1}. {doc.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="signature" className="text-xs">
                        Electronic signature — type your full legal name
                      </Label>
                      <Input
                        id="signature"
                        value={signature}
                        data-testid="register-signature-input"
                        onChange={(e) => setSignature(e.target.value)}
                        placeholder="e.g. John Andrew Smith"
                        className="font-mono"
                      />
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Checkbox
                        id="accept-terms"
                        checked={acceptTerms}
                        onCheckedChange={(value) => setAcceptTerms(value === true)}
                        data-testid="register-terms-checkbox"
                      />
                      <Label htmlFor="accept-terms" className="text-xs leading-relaxed cursor-pointer">
                        I have read and agree to all {legalDocs.length || 18} agreements listed above — including the
                        Terms of Service, Membership Agreement, Privacy Policy, and Refund &amp; Cancellation Policy —
                        and my typed name above is my legal electronic signature applying to each of them.
                      </Label>
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-destructive" role="alert" data-testid="auth-error-message">{error}</p>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting || !registerReady}
                    data-testid="auth-submit-button"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}
